import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/auth';
import { CampaignDetail } from 'src/app/modules/campaigns/models/campaign.models';
import { InstallationMultiPoint } from 'src/app/modules/new-contract-pymes-multipoint/new-contract-pymes-multipoint.model';

@Component({
  selector: 'app-multipoint-calculate-margin',
  templateUrl: './multipoint-calculate-margin.component.html',
  styleUrls: ['./multipoint-calculate-margin.component.scss']
})
export class MultipointCalculateMarginComponent implements OnInit, OnDestroy, OnChanges {
  public enterMarginsForm: FormGroup = new FormGroup({});
  public regexTwoDecimal = new RegExp(/^(\d{1,2}(\,\d{0,3})?|\.?\d{1,2})$/g);
  public showCalculation = false;
  public isUserSpanish = true;
  public resultTP: number[][] = [];
  public resultTE: number[][] = [];
  public resultAI: number[][] = [];
  public resultCG: number[][] = [];
  public resultFijoFlex: number[][] = [];
  public resultFijoFlexSimple: number[][] = [];
  public campaignRealTE: number[][] = [];
  public campaignKI: number[][] = [];
  public campaignFI: number[][] = [];
  public campaignGDOS: number[][] = [];
  public campaignPFijo: number[][] = [];
  public campaignPRef: number[][] = [];
  public campaignTM: number[][] = [];
  public campaignCPM: number[][] = [];
  public campaignCTM: number[][] = [];
  public periods: number[] = [];
  public tabPeriods: number[] = [];
  public fees: string[] = [];
  public tabFees: string[] = [];
  public feeIndex = 0;
  public geographicZones: string[] = [];
  public geographicTabIndex = 0;
  public fijoFlexSimple: boolean = false;

  private campaignCG: number[][] = [];
  private campaignTE: number[][] = [];
  private campaignTP: number[][] = [];
  private campaignAI: number[][] = [];
  private destroyed$: Subject<boolean> = new Subject<boolean>();

  @Input() public isCampaignDetail: boolean = false;
  @Input() public campaignDetailZone: string;
  @Input() public installations: InstallationMultiPoint[] = [];
  @Input() public greaterEqual100: boolean;
  @Input() public campaignDetail: CampaignDetail;
  @Input() public passThrough: boolean;
  @Input() public fijoFlex: boolean;
  @Input() public fixPrice: boolean;
  @Output() public enterMargins: EventEmitter<FormGroup> = new EventEmitter();

  constructor(private authService: AuthService,) { }

  ngOnInit(): void {
    this.isUserSpanish = this.authService.getUserData().country === 'ES';
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.greaterEqual100 && this.installations.length > 0) {
      this.prepareFeesList();
      this.geographicZones = this.installations.map((installation) => {
        if (this.authService.getUserData().country !== 'ES' && !installation.geographicZone) {
          return 'Península';
        } else {
          return installation.geographicZone
        }
      }).filter((value, index, self) => self.indexOf(value) === index);
      this.preparePeriodsList();
      this.buildForm();
      this.getEnterMarginsDraft();
    }
    if (changes.campaignDetail && this.campaignDetail) {
      if (changes.campaignDetail.previousValue) {
        this.enterMarginsForm = new FormGroup({});
        this.buildForm();
        this.showCalculation = false;
      }
      this.campaignDetailChange();
      if (!this.isCampaignDetail && !changes.campaignDetail.previousValue && this.authService.getUserData().country !== 'ES') {
        this.tabFees.forEach((feeName) => {
          for(let i = 1; i < 5; i++) {
            this.enterMarginsForm.controls[feeName].get('marginTEP' + 1).setValue(
              this.installations[0]?.enterMargins?.marginsTE?.p1 ?
              this.installations[0]?.enterMargins?.marginsTE?.p1.toString().replace('.', ',') : null
            );
          }
        });
        this.showCalculation = this.enterMarginsForm.valid;
        if (this.showCalculation) {
          this.calculateMargin();
        }
      }
    }
    if (changes.campaignDetailZone) {
      this.changeGeographicZone(this.geographicZones.findIndex(zone => zone === this.campaignDetailZone));
    }
  }

  private buildForm(): void {
    if (this.authService.getUserData().country === 'ES') {
      this.enterMarginsForm.addControl('marginTP', new FormControl(null, [ Validators.required, Validators.min(0), Validators.max(24), Validators.pattern('([0-9]{1,2}(,[0-9]{1,3})?)') ]));
    }
    const maxValue = this.authService.getUserData().country === 'ES' ? (this.greaterEqual100 ? 24.999 : 49.999) : 25.999;
    this.tabFees.forEach((fee, index) => {
      const marginTE = {};
      for (let i = 0; i < this.tabPeriods[index]; i++) {
        marginTE['marginTEP' + (i + 1)] = new FormControl(null, [ Validators.required, Validators.min(0), Validators.max(maxValue), Validators.pattern('([0-9]{1,2}(,[0-9]{1,3})?)') ]);
      }
      const marginForm = {
        ...marginTE
      };
      this.enterMarginsForm.addControl(fee.toString(), new FormGroup(marginForm));
    });
    if (!this.isCampaignDetail) {
      this.enterMarginsForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.enterMargins.emit(this.enterMarginsForm);
      });
    }
    if (this.authService.getUserData().country !== 'ES') {
      this.enterMarginsForm.controls[this.tabFees[0]].get('marginTEP1').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
        this.tabFees.forEach((controlName, index) => {
          for(let i = 1; i < (this.periods[index] + 1); i++) {
            if (!(index === 0 && i === 1)) {
              this.enterMarginsForm.controls[controlName].get('marginTEP' + i).setValue(value, { emitEvent: false });
            }
          }
        });
      });
    }
  }

  public calculateMargin(): void {
    this.showCalculation = true;
    this.resultTE = this.calcResultTE();
    if (this.authService.getUserData().country === 'ES') {
      this.resultTP = this.calcResultTP();
    }
    this.resultAI = this.calcResultAI();
    this.resultCG = this.calcResultCG();
    if(this.fijoFlexSimple){
      this.resultFijoFlexSimple = this.calcResultFijoFlexSimple();
    } else {
      this.resultFijoFlex = this.calcResultFijoFlex();
    }
  }

  private calcResultTE(): number[][] {
    const resultTE: number[][] = this.fees.map(() => []);
    this.fees.forEach((fee, feeIndex) => {
      this.campaignTE[feeIndex].forEach((valTE, index) => {
        if (fee === '2.0TD' && index > 2) {
          resultTE[feeIndex].push(0);
        } else {
          let feeName = this.authService.getUserData().country === 'ES' ? fee : fee?.split('-')?.pop()?.replace(' ', '');
          if (feeName.includes('3.')) {
            feeName = '3.0TD';
          } else if (feeName.includes('6.')) {
            feeName = '6.XTD';
          }
          const marginTePeriodName = `marginTEP${index + 1}`;
          const marginValue = this.enterMarginsForm.controls[feeName].get(marginTePeriodName)?.value ?? '0';
          let calc = 0;
          if (this.isUserSpanish) {
            calc = ((parseFloat(marginValue.replace(',', '.')) / 1000) * this.campaignTM[feeIndex][index]) +
              (valTE / 1000);
          } else {
            calc = ((parseFloat(marginValue.replace(',', '.')) + valTE) / 1000);
          }
          const resultTEValue = this.enterMarginsForm.controls[feeName].get(marginTePeriodName)?.value ? calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ] : null;
          resultTE[feeIndex].push(parseFloat(resultTEValue));
        }
      });
    });
    return resultTE;
  }

  private calcResultFijoFlex(): number[][] {
    const resultFijoFlex: number[][] = this.fees.map(() => []);
    this.fees.forEach((fee, feeIndex) => {
      this.campaignPFijo[feeIndex].forEach((valPFijo, index) => {
        if (fee === '2.0TD' && index > 2) {
          resultFijoFlex[feeIndex].push(0);
        } else {
          let feeName = this.authService.getUserData().country === 'ES' ? fee : fee?.split('-')?.pop()?.replace(' ', '');
          if (feeName.includes('3.')) {
            feeName = '3.0TD';
          } else if (feeName.includes('6.')) {
            feeName = '6.XTD';
          }
          const marginTePeriodName = `marginTEP${index + 1}`;
          const marginValue = this.enterMarginsForm.controls[feeName].get(marginTePeriodName)?.value ?? '0';
          let calc = null;
          if (this.isUserSpanish) {
            calc = ((parseFloat(marginValue.replace(',', '.')) / 1000) * this.campaignTM[feeIndex][index]) +
            (valPFijo / 1000);
          } else {
            calc = (parseFloat(marginValue.replace(',', '.')) / 1000) +
            (valPFijo / 1000);
          }
          const resultPFijoValue = this.enterMarginsForm.controls[feeName].get(marginTePeriodName)?.value ? calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ] : null;
          resultFijoFlex[feeIndex].push(parseFloat(resultPFijoValue));
        }
      });
    });
    return resultFijoFlex;
  }

  private calcResultFijoFlexSimple(): number[][] {
    const resultFijoFlexSimple: number[][] = this.fees.map(() => []);
    this.fees.forEach((fee, feeIndex) => {
      this.campaignPRef[feeIndex].forEach((valPFijoSimple, index) => {
        if (fee === '2.0TD' && index > 2) {
          resultFijoFlexSimple[feeIndex].push(0);
        } else {
          let feeName = this.authService.getUserData().country === 'ES' ? fee : fee?.split('-')?.pop()?.replace(' ', '');
          if (feeName.includes('3.')) {
            feeName = '3.0TD';
          } else if (feeName.includes('6.')) {
            feeName = '6.XTD';
          }
          const marginTePeriodName = `marginTEP${index + 1}`;
          const marginValue = this.enterMarginsForm.controls[feeName].get(marginTePeriodName)?.value ?? '0';
          let calc = null;
          if (this.isUserSpanish) {
            calc = ((parseFloat(marginValue.replace(',', '.')) / 1000) * this.campaignTM[feeIndex][index]) +
            (valPFijoSimple / 1000);
          } else {
            calc = valPFijoSimple / 1000;
          }
          const resultPRefValue = this.enterMarginsForm.controls[feeName].get(marginTePeriodName)?.value ? calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ] : null;
          resultFijoFlexSimple[feeIndex].push(parseFloat(resultPRefValue));
        }
      });
    });
    return resultFijoFlexSimple;
  }

  private calcResultAI(): number[][] {
    const resultAI: number[][] = this.fees.map(() => []);
    this.fees.forEach((fee, feeIndex) => {
      this.campaignAI[feeIndex].forEach((valAI, index) => {
        if (fee === '2.0TD' && index > 2) {
          resultAI[feeIndex].push(0);
        } else {
          let feeName = this.authService.getUserData().country === 'ES' ? fee : fee?.split('-')?.pop()?.replace(' ', '');
          if (feeName.includes('3.')) {
            feeName = '3.0TD';
          } else if (feeName.includes('6.')) {
            feeName = '6.XTD';
          }
          const marginTePeriodName = `marginTEP${index + 1}`;
          const marginValue = this.enterMarginsForm.controls[feeName].get(marginTePeriodName)?.value ?? '0';
          let calc = 0;
          if (this.isUserSpanish) {
            calc = ((parseFloat(marginValue.replace(',', '.')) / 1000) * ((1 + this.campaignCTM[feeIndex][index]) * (1 + this.campaignCPM[feeIndex][index]))) +
            (valAI / 1000);
          } else {
            calc = ((parseFloat(marginValue.replace(',', '.')) + valAI) / 1000);
          }
          const resultAIValue = this.enterMarginsForm.controls[feeName].get(marginTePeriodName)?.value ? calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ] : null;
          resultAI[feeIndex].push(parseFloat(resultAIValue));
        }
      });
    });
    return resultAI;
  }

  private calcResultTP(): number[][] {
    const resultTP: number[][] = this.fees.map(() => []);
    this.fees.forEach((_fee, index) => {
      this.campaignTP[index].forEach((valTP) => {
        if (valTP === null) {
          resultTP[index].push(null);
        } else {
          const marginValue = this.enterMarginsForm.get('marginTP')?.value ?? '0';
          const calc = parseFloat(marginValue.replace(',', '.')) + valTP;
          resultTP[index].push(parseFloat(calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]));
        }
      });
    });
    return resultTP;
  }

  private calcResultCG(): number[][] {
    const resultCG: number[][] = this.fees.map(() => []);
    this.fees.forEach((fee, feeIndex) => {
      this.campaignCG[feeIndex].forEach((valCG, index) => {
        if (valCG === null) {
          resultCG[feeIndex].push(null);
        } else {
          let feeName = this.authService.getUserData().country === 'ES' ? fee : fee?.split('-')?.pop()?.replace(' ', '');
          if (feeName.includes('3.')) {
            feeName = '3.0TD';
          } else if (feeName.includes('6.')) {
            feeName = '6.XTD';
          }
          if (this.authService.getUserData().country === 'ES') {
            resultCG[feeIndex].push(+this.enterMarginsForm?.controls[feeName]?.get('marginTEP' + (index + 1))?.value?.toString().replace(',','.'));
          } else {
            resultCG[feeIndex].push(valCG + +this.enterMarginsForm?.controls[feeName]?.get('marginTEP' + (index + 1))?.value?.toString().replace(',','.'));
          }
        }
      });
    });
    return resultCG;
  }

  private findFee(): void {
    this.campaignTE = [];
    this.campaignRealTE = [];
    this.campaignTP = [];
    this.campaignAI = [];
    this.campaignKI = [];
    this.campaignFI = [];
    this.campaignCG = [];
    this.campaignGDOS = [];
    this.campaignPFijo = [];
    this.campaignPRef = [];
    this.campaignTM = [];
    this.campaignCTM = [];
    this.campaignCPM = [];
    this.fees.forEach((fee, index) => {
      if (fee.includes('S/C - ')) {
        this.fees[ index ] = fee.replace('S/C - ', '');
      }
      const feeCampaignTE = [];
      const feeCampaignRealTE = [];
      const feeCampaignTP = [];
      const feeCampaignAI = [];
      const feeCampaignKI = [];
      const feeCampaignFI = [];
      const feeCampaignCG = [];
      const feeCampaignGDOS = [];
      const feeCampaignPFijo = [];
      const feeCampaignPRef = [];
      const feeCampaignTM = [];
      const feeCampaignCTM = [];
      const feeCampaignCPM = [];
      const powerTerms = this.campaignDetail?.zonasGeograficas.find(
        zone => zone.nombreZona === this.geographicZones[this.geographicTabIndex]
      ).tarifas.find(el => el.nombreTarifa === fee);
      if (powerTerms[ `pRef_P1` ] !== null ) {
        this.fijoFlexSimple = true;
      } else {
        this.fijoFlexSimple = false;
      }
      for (let i = 0; i < 6; i++) {
        if (!this.isUserSpanish) {
          feeCampaignTE.push(powerTerms[`precio_P${i + 1}`] === null ? null : powerTerms[`precio_P${i + 1}`]);
        } else {
          feeCampaignTE.push(powerTerms[`precioFinal_P${i + 1}`] === null ? null : powerTerms[`precioFinal_P${i + 1}`]);  
        }
        feeCampaignRealTE.push((powerTerms[ `tE_P${i + 1}` ] === null ? null : powerTerms[ `tE_P${i + 1}` ]) /1000);
        feeCampaignTP.push(powerTerms[ `tP_P${i + 1}` ] === null ? null : powerTerms[ `tP_P${i + 1}` ]);
        feeCampaignAI.push(powerTerms[ `ai_P${i + 1}` ] === null ? null : powerTerms[ `ai_P${i + 1}` ]);
        feeCampaignKI.push(powerTerms[ `ki_P${i + 1}` ] === null ? null : powerTerms[ `ki_P${i + 1}` ]);
        feeCampaignFI.push(powerTerms[ `fi_P${i + 1}` ] === null ? null : powerTerms[ `fi_P${i + 1}` ]);
        feeCampaignCG.push(powerTerms[ `cG_P${i + 1}` ] === null ? null : powerTerms[ `cG_P${i + 1}` ]);
        feeCampaignGDOS.push(powerTerms[ `gdOs_P${i + 1}` ] === null ? null : powerTerms[ `gdOs_P${i + 1}` ]);
        feeCampaignPFijo.push(powerTerms[ `pFijo_P${i + 1}` ] === null ? null : powerTerms[ `pFijo_P${i + 1}` ]);
        feeCampaignPRef.push(powerTerms[ `pRef_P${i + 1}` ] === null ? null : powerTerms[ `pRef_P${i + 1}` ]);
        feeCampaignTM.push(powerTerms[ `tM_P${i + 1}` ] === null ? null : powerTerms[ `tM_P${i + 1}` ]);
        feeCampaignCTM.push(powerTerms[ `ctM_P${i + 1}` ] === null ? null : powerTerms[ `ctM_P${i + 1}` ]);
        feeCampaignCPM.push(powerTerms[ `cpM_P${i + 1}` ] === null ? null : powerTerms[ `cpM_P${i + 1}` ]);
      }
      this.campaignTE.push(feeCampaignTE);
      this.campaignRealTE.push(feeCampaignRealTE);
      this.campaignTP.push(feeCampaignTP);
      this.campaignAI.push(feeCampaignAI);
      this.campaignKI.push(feeCampaignKI);
      this.campaignFI.push(feeCampaignFI);
      this.campaignCG.push(feeCampaignCG);
      this.campaignGDOS.push(feeCampaignGDOS);
      this.campaignPFijo.push(feeCampaignPFijo);
      this.campaignPRef.push(feeCampaignPRef);
      this.campaignTM.push(feeCampaignTM);
      this.campaignCTM.push(feeCampaignCTM);
      this.campaignCPM.push(feeCampaignCPM);
    });
    if (this.showCalculation && this.enterMarginsForm.valid) {
      this.calculateMargin();
    }
  }

  public changeGeographicZone(geographicIndex: number): void {
    this.geographicTabIndex = geographicIndex;
    this.resultTE = [];
    this.resultTP = [];
    this.resultAI = [];
    this.resultCG = [];
    this.findFee();
    if (this.showCalculation && this.enterMarginsForm.valid) {
      this.calculateMargin();
    }
  }

  public changeFee(feeIndex: number): void {
    this.geographicTabIndex = 0;
    this.feeIndex = feeIndex;
    this.findFee();
    if (this.showCalculation) {
      this.changeGeographicZone(this.geographicTabIndex);
    }
  }

  private getEnterMarginsDraft(): void {
    Object.keys(this.enterMarginsForm.controls).forEach(key => {
      if (key !== 'marginTP') {
        const installation = this.installations.find(inst => {
          if (this.authService.getUserData().country !== 'ES') {
            if (
              inst?.tension?.toString()?.length > 0 &&
              inst?.cycleName?.toString()?.length > 0 &&
              inst?.periodName?.toString()?.length > 0
            ) {
              return (inst.tension + ' - ' + inst.cycleName.split(' - ')[ 0 ] + ' - ' + inst.periodName).includes(key);
            } else {
              return inst.feeName ? inst.feeName.includes(key) : inst.fee.includes(key);
            }
          } else {
            let feeName = inst.fee;
            if (feeName.includes('3.')) {
              feeName = '3.0TD';
            } else if (feeName.includes('6.')) {
              feeName = '6.XTD';
            }
            return feeName === key;
          }
        });
        for (let i = 1; i < 7; i++) {
          if (this.enterMarginsForm.controls[ key ].get('marginTEP' + i)) {
            const pName = 'p' + i;
            if (installation.enterMargins?.marginsTE) {
              this.enterMarginsForm.controls[ key ].get('marginTEP' + i).setValue(
                installation.enterMargins?.marginsTE[pName]?.toString().replace('.', ','), { emitEvent: false }
              );
            } else {
              this.enterMarginsForm.controls[ key ].get('marginTEP' + i).setValue(null, { emitEvent: false });
            }
          }
        }
      } else {
        if (this.authService.getUserData().country === 'ES' && this.installations.length > 0) {
          this.enterMarginsForm.get('marginTP').setValue(this.installations[0].enterMargins?.marginTP?.toString().replace('.', ','), { emitEvent: false });
        }
      }
    });
    this.enterMarginsForm.updateValueAndValidity();
    this.showCalculation = this.enterMarginsForm.valid;
  }

  private campaignDetailGetPeriod(): void {
    if (this.authService.getUserData().country === 'ES') {
      this.periods = this.fees.map((fee) => fee !== '2.0TD' ? 6 : 3);
      this.tabPeriods = this.tabFees.map((fee) => fee !== '2.0TD' ? 6 : 3);
    } else {
      this.ptGetPeriods();
    }
  }

  private prepareFeesList(): void {
    this.fees = this.installations.map((installation) => {
      if (this.authService.getUserData().country !== 'ES') {
        if (
          installation?.tension?.toString()?.length > 0 &&
          installation?.cycleName?.toString()?.length > 0 &&
          installation?.periodName?.toString()?.length > 0
        ) {
          return installation?.tension + ' - ' + installation?.cycleName?.split(' - ')[ 0 ] + ' - ' + installation?.periodName;
        } else {
          return installation.feeName ?? installation.fee;
        }
      } else {
        return installation.fee;
      }
    }).filter((value, index, self) => self.indexOf(value) === index);
    this.tabFees = this.fees.map(fee => {
      if (this.authService.getUserData().country === 'ES') {
        if (fee.includes('3.')) {
          return '3.0TD'
        } else if (fee.includes('6.')) {
          return '6.XTD'
        } else {
          return fee;
        }
      } else {
        return fee?.split('-')?.pop()?.replace(' ', '');
      }
    }).filter((value, index, self) => self.indexOf(value) === index);
    this.sortAndGroupingFee();
  }

  private preparePeriodsList(): void {
    if (this.authService.getUserData().country === 'ES') {
      this.periods = this.fees.map((fee) => fee !== '2.0TD' ? 6 : 3);
      this.tabPeriods = this.tabFees.map((fee) => fee !== '2.0TD' ? 6 : 3);
    } else {
      this.ptGetPeriods();
    }
  }

  private campaignDetailChange(): void {
    if (!this.isCampaignDetail && this.isUserSpanish) {
      this.getEnterMarginsDraft();
    } else {
      this.geographicZones = this.campaignDetail?.zonasGeograficas.map((zone) => zone.nombreZona);
      this.geographicTabIndex = 0;
      this.fees = this.campaignDetail?.zonasGeograficas[this.geographicTabIndex].tarifas.map((tarifa) => tarifa.nombreTarifa);
      this.tabFees = this.fees.map(fee => {
        if (this.authService.getUserData().country === 'ES') {
          if (fee.includes('3.')) {
            return '3.0TD'
          } else if (fee.includes('6.')) {
            return '6.XTD'
          } else {
            return fee;
          }
        } else {
          return fee?.split('-')?.pop()?.replace(' ', '');
        }
      }).filter((value, index, self) => self.indexOf(value) === index);
      this.sortAndGroupingFee();
      this.feeIndex = 0;
      this.campaignDetailGetPeriod();
      this.buildForm();
    }
    this.findFee();
  }

  private sortAndGroupingFee(): void {
    if (this.authService.getUserData().country === 'ES') {
      this.fees.sort((a, b) => a < b ? -1 : 1);
      this.tabFees.sort((a, b) => a < b ? -1 : 1);
    } else {
      const ptFeeOrder = ['Simples', 'Dupla', 'Tripla', 'Tetra'];
      const ptFee = [];
      ptFeeOrder.forEach(fee => {
        if(this.tabFees.includes(fee)) {
          ptFee.push(fee);
        }
      });
      this.tabFees = ptFee;
    }
  }

  private ptGetPeriods(): void {
    this.periods = this.fees.map((fee) => {
      // if (fee.includes('Simples')) {
      //   return 1;
      // } else if (fee.includes('Dupla')) {
      //   return 2;
      // } else if (fee.includes('Tripla')) {
      //   return 3;
      // } else {
        return 4;
      // }
    });
    this.tabPeriods = this.tabFees.map((fee) => {
      // if (fee.includes('Simples')) {
      //   return 1;
      // } else if (fee.includes('Dupla')) {
      //   return 2;
      // } else if (fee.includes('Tripla')) {
      //   return 3;
      // } else {
        return 4;
      // }
    });
  }
}
